import { useEffect, useState } from 'react';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { ApiRequestError } from '@/api/errors';
import { payApiClient } from '@/api/client';
import type { TenantTagItem } from '@/api/models';

interface TenantTagsDialogProps {
  open: boolean;
  tenantCode: string;
  initialTags: TenantTagItem[];
  onClose: () => void;
  onSaved: (tags: TenantTagItem[]) => void;
}

const TAG_KEY_PATTERN = /^[a-z0-9_-]+$/;

/**
 * `PUT /v1/admin/tenants/{code}/tags` — its own contract operation, replacing
 * the tenant's entire tag set (never a merge). `initialTags` is the tenant's
 * actual saved tag set, read from `AdminTenant.tags` (A3 contract addition),
 * so a replace here is a genuine edit rather than a blind overwrite.
 */
export function TenantTagsDialog({ open, tenantCode, initialTags, onClose, onSaved }: TenantTagsDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const [tags, setTags] = useState<TenantTagItem[]>(initialTags);
  const [accessDenied, setAccessDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setTags(initialTags);
      setAccessDenied(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function updateTag(index: number, patch: Partial<TenantTagItem>) {
    setTags((prev) => prev.map((tag, i) => (i === index ? { ...tag, ...patch } : tag)));
  }

  function removeTag(index: number) {
    setTags((prev) => prev.filter((_, i) => i !== index));
  }

  function addTag() {
    setTags((prev) => [...prev, { key: '', value: '' }]);
  }

  const hasInvalidTag = tags.some((tag) => !TAG_KEY_PATTERN.test(tag.key) || tag.value.trim().length === 0);

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const response = await payApiClient.updateAdminTenantTags(tenantCode, { tags });
      notify(translate('pay.tenants.tags.success'), { type: 'success' });
      onSaved(response.tags);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.errorCode === 'access_denied') {
        setAccessDenied(true);
      } else {
        const message = caught instanceof ApiRequestError ? caught.message : undefined;
        notify(message ?? translate('pay.errorState.body'), { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translate('pay.tenants.tags.title')}</DialogTitle>
      <DialogContent>
        {accessDenied ? (
          <AccessDenied />
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Alert severity="info">{translate('pay.tenants.tags.replaceNotice')}</Alert>
            <Stack spacing={1.5}>
              {tags.map((tag, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    size="small"
                    label={translate('pay.tenants.tags.fieldKey')}
                    helperText={translate('pay.tenants.tags.keyHelp')}
                    value={tag.key}
                    error={tag.key.length > 0 && !TAG_KEY_PATTERN.test(tag.key)}
                    onChange={(event) => updateTag(index, { key: event.target.value })}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label={translate('pay.tenants.tags.fieldValue')}
                    value={tag.value}
                    onChange={(event) => updateTag(index, { value: event.target.value })}
                    fullWidth
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeTag(index)}
                    aria-label={translate('pay.tenants.tags.remove')}
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Button size="small" startIcon={<AddOutlinedIcon fontSize="small" />} onClick={addTag} sx={{ alignSelf: 'flex-start' }}>
              {translate('pay.tenants.tags.add')}
            </Button>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('pay.tenants.tags.cancel')}</Button>
        {!accessDenied && (
          <Button variant="contained" onClick={handleSubmit} disabled={submitting || hasInvalidTag}>
            {translate('pay.tenants.tags.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
